import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { logger } from '../services/loggingService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Two sinks: console for DevTools-on-localhost debugging, and the
    // unified loggingService which batches to /client_logs → Cloud
    // Logging. Without the latter, prod React crashes are invisible
    // server-side — users see "Something went wrong" while we see
    // nothing in Logs Explorer.
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    logger.error('React ErrorBoundary caught error', {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack?.split('\n').slice(0, 10).join('\n'),
      componentStack: errorInfo.componentStack?.split('\n').slice(0, 15).join('\n'),
      url: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
    this.setState({ error, errorInfo });
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: 'center',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <ErrorOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                Something went wrong
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                The application encountered an unexpected error. You can try again without reloading.
              </Typography>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ mt: 1, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'left' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Error Details:
                  </Typography>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{ overflow: 'auto', color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-wrap' }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={this.handleRetry}
                  sx={{
                    bgcolor: 'text.primary',
                    color: 'background.paper',
                    textTransform: 'none',
                    '&:hover': { opacity: 0.85 },
                  }}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={this.handleReload}
                  sx={{
                    borderColor: 'divider',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': { borderColor: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  Reload Page
                </Button>
              </Box>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
